import React from "react";
import {
  ArrowRight,
  CalendarBlank,
  CaretLeft,
  CaretRight,
  FileText,
  MapPin,
  UserCircle,
} from "@phosphor-icons/react";

import { INSTALLATION_STAGE, INSTALLATION_STATUS } from "@/lib/gmaoInstallationStatus";


function formatDate(value) {
  if (!value) return "Date non planifiée";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(parsed);
}


function personName(person = {}) {
  return [person.firstName, person.lastName].filter(Boolean).join(" ")
    || person.email
    || "Non affecté";
}


function clientIdentity(item) {
  const contact = item.contact || item.client || {};
  const person = [contact.firstName, contact.lastName].filter(Boolean).join(" ");
  const company = contact.company || item.companyName || item.clientCompany || "";
  return {
    person: person || (!company ? item.clientName : "") || "Client non renseigné",
    company,
  };
}


function machineName(item) {
  return item.machineName || item.machine?.name || item.machine?.model || "Machine à préciser";
}


function locationLabel(item) {
  const contact = item.contact || item.client || {};
  const machine = item.machine || {};
  const site = item.siteName || machine.siteName || contact.siteName || contact.company || "";
  const address = item.address || machine.address || contact.address || "";
  const postal = item.postalCode || item.postal_code || machine.postalCode || contact.postalCode || contact.postal_code || "";
  const city = item.city || machine.city || contact.city || "";
  const addressLine = [address, [postal, city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return [site, addressLine].filter(Boolean).join(" · ") || "Site à renseigner";
}


function statusLabel(item, stage) {
  if (stage === INSTALLATION_STAGE.CONFIRMATION) {
    if (item.deliveryConfirmationStatus === "confirmed") return "Client confirmé";
    if (item.deliveryConfirmationStatus === "reschedule_requested") return "Nouvelle date";
    return item.deliveryShareId ? "En attente client" : "BL à envoyer";
  }
  if (stage === INSTALLATION_STAGE.PROGRESS) return "En installation";
  if (stage === INSTALLATION_STAGE.DONE) return item.status === INSTALLATION_STATUS.ARCHIVED ? "Archivée" : "Terminée";
  return item.serialNumber ? "Préparation en cours" : "À compléter";
}


function InstallationCard({ item, stage, assigneeById, onOpen }) {
  const client = clientIdentity(item);
  const location = locationLabel(item);
  const targetDate = item.scheduledDate || item.scheduledStart || item.desiredDate || item.installationDate;
  const assignee = assigneeById[item.assignedUserId || item.technicianId] || item.technician || {};
  const resolvedAssigneeName = personName(assignee);
  const assigneeName = resolvedAssigneeName !== "Non affecté"
    ? resolvedAssigneeName
    : item.technicianName || "Non affecté";

  return (
    <article
      className="group rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4 text-left text-[var(--do-ink)] transition hover:-translate-y-0.5 hover:border-[var(--do-accent)] hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--do-accent)]"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(item.id)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen(item.id);
        }
      }}
      data-testid={`installation-kanban-card-${item.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <strong className="block truncate font-heading text-sm font-black">{machineName(item)}</strong>
          <span className="mt-1 block truncate text-xs font-semibold text-[var(--do-muted)]">{client.person}</span>
          {client.company && <span className="mt-0.5 block truncate text-xs text-[var(--do-muted)]">{client.company}</span>}
        </div>
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-2.5 py-1 text-xs font-bold text-[var(--do-accent)]">
          <FileText size={13} weight="bold" /> {statusLabel(item, stage)}
        </span>
      </div>

      <div className="mt-3 grid gap-2 text-xs text-[var(--do-muted)]">
        <span className="flex min-w-0 items-center gap-2"><UserCircle size={15} weight="bold" /><span className="truncate">{assigneeName}</span></span>
        <span className="flex min-w-0 items-center gap-2"><CalendarBlank size={15} weight="bold" /><span className="truncate">{formatDate(targetDate)}</span></span>
        <span className="flex min-w-0 items-center gap-2"><MapPin size={15} weight="bold" /><span className="truncate" title={location}>{location}</span></span>
        {item.status === INSTALLATION_STATUS.ARCHIVED && item.archiveReason ? <span className="rounded-lg border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-2 text-[var(--do-text)]">Motif : {item.archiveReason}</span> : null}
      </div>

      <footer className="mt-3 flex items-center justify-between gap-3 border-t border-[var(--do-border)] pt-3 text-xs font-bold">
        <span className="truncate">Ouvrir l’installation</span>
        <ArrowRight size={15} weight="bold" className="shrink-0 transition-transform group-hover:translate-x-0.5" />
      </footer>
    </article>
  );
}


export default function InstallationKanbanColumn({
  column,
  data,
  currentPage,
  pageSize,
  loading = false,
  assigneeById = {},
  onOpen,
  onPageChange,
}) {
  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const Icon = column.icon;

  return (
    <section className="pipeline-column pipeline-final-column min-w-0" data-testid={`installation-kanban-column-${column.key}`}>
      <header className="pipeline-column-header pipeline-final-column-header">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: column.color }} />
            <h2 className="font-heading text-xs font-black text-[var(--do-ink)]">{column.label}</h2>
          </div>
          <span className="rounded-full bg-[var(--do-surface-soft)] px-2 py-0.5 text-xs font-bold text-[var(--do-muted)]">{data.total}</span>
        </div>
        <p className="mt-2 text-xs text-[var(--do-muted)]">{column.description}</p>
      </header>

      <div className="pipeline-column-body pipeline-final-column-body">
        {loading ? (
          <div className="pipeline-premium-empty-state">Chargement…</div>
        ) : !data.total ? (
          <div className="pipeline-premium-empty-state">
            <Icon size={28} weight="duotone" />
            <strong>Aucune installation</strong>
          </div>
        ) : (
          data.items.map((item) => (
            <InstallationCard
              key={item.id}
              item={item}
              stage={column.key}
              assigneeById={assigneeById}
              onOpen={onOpen}
            />
          ))
        )}
      </div>

      {data.total > pageSize && (
        <footer className="flex items-center justify-between border-t border-[var(--do-border)] p-2.5">
          <button
            type="button"
            disabled={safePage <= 1 || loading}
            onClick={() => onPageChange(safePage - 1)}
            className="rounded-lg border border-[var(--do-border)] p-1.5 disabled:opacity-40"
            aria-label={`Page précédente ${column.label}`}
          >
            <CaretLeft size={15} />
          </button>
          <span className="text-xs font-bold text-[var(--do-muted)]">{safePage}/{totalPages}</span>
          <button
            type="button"
            disabled={safePage >= totalPages || loading}
            onClick={() => onPageChange(safePage + 1)}
            className="rounded-lg border border-[var(--do-border)] p-1.5 disabled:opacity-40"
            aria-label={`Page suivante ${column.label}`}
          >
            <CaretRight size={15} />
          </button>
        </footer>
      )}
    </section>
  );
}
